import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import GeoPiButton from "@/components/GeoPiButton";
import { Satellite, Map as MapIcon, Activity, ArrowUpRight, LayoutGrid, LayoutList } from "lucide-react";

const PENDING_KEY = "symin-pending-module";

const RS_IMG = "https://images.unsplash.com/photo-1573490647684-928a2454f861?crop=entropy&cs=srgb&fm=jpg&q=85&w=700&h=900&fit=crop";
const MAP_IMG = "https://images.unsplash.com/photo-1730314737142-2f6bb293f893?crop=entropy&cs=srgb&fm=jpg&q=85&w=700&h=900&fit=crop";
const GEO_IMG = "https://images.unsplash.com/photo-1762166206312-bfa56b08d690?crop=entropy&cs=srgb&fm=jpg&q=85&w=700&h=900&fit=crop";

const MODULES = [
  { no: "01", key: "remote-sensing", route: "/remote-sensing", icon: Satellite, img: RS_IMG,
    title: "REMOTE SENSING", sub: "Satellite-Based Mineral Exploration",
    desc: "Spectral, alteration and structural mapping that produces ranked exploration targets.",
    tags: ["Imagery", "Indices", "Prospectivity"] },
  { no: "02", key: "maps", route: "/maps", icon: MapIcon, img: MAP_IMG,
    title: "GEOMAPPING", sub: "Terrain & GIS Mapping",
    desc: "DEM, hillshade, contour, drainage and topography sheets, print-ready and georeferenced.",
    tags: ["DEM", "Topography", "Mapping"] },
  { no: "03", key: "geophysics", route: "/geophysics", icon: Activity, img: GEO_IMG,
    title: "GEOPHYSICS", sub: "Geophysical Data Processing & Interpretation",
    desc: "Magnetic, gravity, IP/resistivity and EM data gridded, enhanced and interpreted.",
    tags: ["Magnetic", "IP/Resistivity", "Modelling"] },
];

const PLATFORM = [
  { title: "One workspace", desc: "Every module shares a single project context, so imagery, maps and geophysics stay in sync." },
  { title: "Approved access", desc: "Accounts are reviewed and approved before sign-in, keeping client data private by default." },
  { title: "Module permissions", desc: "Fine-grained roles decide who can open, process and export in each discipline." },
  { title: "Audited", desc: "Every action is logged, so targets and reports remain traceable and defensible." },
];

const ModuleCard = ({ m, onOpen, list }) => {
  const Icon = m.icon;
  return (
    <div data-testid={`module-card-${m.key}`}
      className={`group rounded-2xl border border-slate-200 bg-white hover:border-[#C20511] transition-colors duration-200 flex overflow-hidden ${list ? "h-32" : "h-36"}`}>
      <div className="flex-1 min-w-0 p-3.5 flex flex-col">
        <div className="flex items-start gap-2.5">
          <span className="h-8 w-8 rounded-lg bg-slate-900 grid place-items-center shrink-0">
            <Icon size={16} className="text-white" />
          </span>
          <div className="min-w-0 flex-1">
            <h3 className="text-sm font-bold text-slate-900 tracking-wide leading-tight">{m.title}</h3>
            <div className="text-[11px] font-semibold text-[#C20511] truncate">{m.sub}</div>
          </div>
        </div>
        <p className="text-xs text-slate-500 leading-snug mt-2 truncate">{m.desc}</p>
        <div className="mt-auto flex flex-nowrap gap-1.5 overflow-hidden">
          {m.tags.map((t) => (
            <span key={t} className="shrink-0 rounded-full border border-slate-200 bg-slate-50 px-2.5 py-0.5 text-[10px] font-semibold text-slate-600 whitespace-nowrap">{t}</span>
          ))}
        </div>
      </div>
      <div className={`relative shrink-0 ${list ? "w-40 sm:w-48" : "w-20 sm:w-24"}`}>
        <img src={m.img} alt={m.title} className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-slate-900/10" />
        <span className="absolute top-2 left-2 font-display text-xs font-bold text-white/95 drop-shadow">{m.no}</span>
        <button onClick={() => onOpen(m.route)} data-testid={`module-open-${m.key}`}
          title={`Open ${m.title}`}
          className="absolute bottom-2.5 right-2.5 h-9 w-9 rounded-full bg-[#C20511] text-white grid place-items-center shadow-lg hover:bg-[#9E040E] hover:scale-105 transition-all">
          <ArrowUpRight size={18} />
        </button>
      </div>
    </div>
  );
};

export default function Landing() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [view, setView] = useState("grid");

  const openModule = (route) => {
    if (user) { nav(route); return; }
    localStorage.setItem(PENDING_KEY, route);
    nav("/login");
  };
  const goLogin = () => { localStorage.removeItem(PENDING_KEY); nav("/login"); };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-700 antialiased" data-testid="landing-page">
      {/* navbar */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between gap-4">
          <Link to="/" className="shrink-0 flex items-center" data-testid="landing-logo-link">
            <img src="/symin-logo.png" alt="SYMIN" className="h-9 sm:h-10 w-auto object-contain" data-testid="landing-logo" />
          </Link>
          <nav className="hidden md:flex items-center gap-8 text-sm font-semibold text-slate-600">
            <a href="#modules" className="hover:text-[#C20511] transition-colors">Modules</a>
            <a href="#platform" className="hover:text-[#C20511] transition-colors">Platform</a>
            <a href="#about" className="hover:text-[#C20511] transition-colors">About</a>
          </nav>
          <div className="flex items-center gap-2 shrink-0">
            <GeoPiButton className="hidden sm:inline-flex" />
            <button onClick={goLogin} data-testid="nav-login"
              className="rounded-full border border-slate-300 px-3.5 sm:px-5 py-2 text-xs sm:text-sm font-semibold text-slate-700 hover:border-[#C20511] hover:text-[#C20511] transition-colors whitespace-nowrap">Login</button>
            <Link to="/register" data-testid="nav-register"
              className="rounded-full bg-[#C20511] px-3.5 sm:px-5 py-2 text-xs sm:text-sm font-bold text-white hover:bg-[#9E040E] transition-colors whitespace-nowrap">Create Account</Link>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-5 sm:px-8 py-10 sm:py-14">
        {/* modules card */}
        <section id="modules" className="rounded-3xl border border-slate-200 bg-white p-6 sm:p-10">
          <div className="flex items-start justify-between gap-4 mb-8">
            <div>
              <h1 className="font-display text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight">Exploration Modules</h1>
              <p className="text-slate-500 mt-2">Three independent workflows, one project context.</p>
            </div>
            <div className="flex rounded-lg border border-slate-200 overflow-hidden shrink-0">
              <button data-testid="view-grid" onClick={() => setView("grid")} title="Grid view"
                className={`h-9 w-10 grid place-items-center ${view === "grid" ? "bg-slate-900 text-white" : "bg-white text-slate-500 hover:text-slate-900"}`}><LayoutGrid size={16} /></button>
              <button data-testid="view-list" onClick={() => setView("list")} title="List view"
                className={`h-9 w-10 grid place-items-center ${view === "list" ? "bg-slate-900 text-white" : "bg-white text-slate-500 hover:text-slate-900"}`}><LayoutList size={16} /></button>
            </div>
          </div>
          <div className={view === "grid" ? "grid grid-cols-1 md:grid-cols-3 gap-4" : "space-y-4"}>
            {MODULES.map((m) => <ModuleCard key={m.key} m={m} onOpen={openModule} list={view === "list"} />)}
          </div>
        </section>

        {/* platform */}
        <section id="platform" className="mt-14 sm:mt-20">
          <div className="text-[11px] font-bold tracking-[0.22em] uppercase text-[#C20511] mb-6">Platform</div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {PLATFORM.map((p) => (
              <div key={p.title} className="pl-4 border-l-2 border-[#C20511]">
                <h3 className="text-base font-bold text-slate-900">{p.title}</h3>
                <p className="text-sm text-slate-500 leading-relaxed mt-1.5">{p.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* about */}
        <section id="about" className="mt-14 sm:mt-20">
          <div className="rounded-3xl border border-slate-200 bg-white p-8 sm:p-10">
            <div>
              <div className="text-[11px] font-bold tracking-[0.22em] uppercase text-[#C20511] mb-3">About</div>
              <h2 className="font-display text-3xl font-bold text-slate-900 tracking-tight mb-4">SYMIN</h2>
              <p className="text-base text-slate-600 leading-relaxed">
                SYMIN is an exploration intelligence platform that turns multi-sensor satellite data,
                high-resolution mapping and geophysical surveys into ranked, defensible exploration targets and
                automated, standards-ready technical reports, giving geoscientists one auditable workspace from first pixel to signed target.
              </p>
            </div>
          </div>
        </section>
      </main>

      {/* footer */}
      <footer className="border-t border-slate-200 bg-slate-100">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-5 flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="flex items-center gap-3 order-1">
            <img src="/symin-logo.png" alt="SYMIN" className="h-6 w-auto object-contain" />
            <div className="text-[11px] text-slate-400" data-testid="footer-product">A product of Shanvi Resources</div>
          </div>
          <div className="order-3 sm:order-2 text-[11px] font-bold text-slate-400 tracking-[0.3em] uppercase text-center" data-testid="footer-center-tagline">
            SCAN · MAP · TARGET
          </div>
          <div className="order-2 sm:order-3 text-[12px] text-slate-400">© {new Date().getFullYear()} SYMIN</div>
        </div>
      </footer>
    </div>
  );
}
